import BusinessModel from '@/Models/businessModel';
import CameraSummaryModel from '@/Models/cameraSummaryModel';
import RequestAssigneeSummaryModel from '@/Models/requestAssigneeSummaryModel';
import PageResultModel from '@/Models/pageResultModel';
import apiClient from '@/Services/Clients/apiClient';
import { useContextDataStore } from '@/VueCore/stores/contextDataStore';
import PageResult from '@/Types/pageResult';
import CameraSummary from '@/Types/cameraSummary';
import { ApplicationError } from '@/Services/Clients/applicationError';

const BUSINESS_BASE_URL: string = 'api/business';
class BusinessService {

  public static updateBusinessData(businessData) {
    const contextData = useContextDataStore();
    const business = contextData.userData.business;
    const resourceUrl = `${BUSINESS_BASE_URL}/${business.businessId}`;

    return new Promise(function (resolve, reject) {
      apiClient.put(resourceUrl, businessData)
        .then(function (serverBusinessData) {
          const businessData = new BusinessModel(serverBusinessData);
          resolve(businessData);
        })
        .catch((error: ApplicationError) => {
          reject(error);
        });
    });
  }

  /**
   * Retrieve the cameras for the business with the optional sorting and paging parameters.
   * @param {string} [sortField] String value of the CameraSortField enum
   * @param {string} [sortOrder] String value of the SortOrder enum
   * @param {number} [page] Page number to retrieve results for
   * @param {string} [previousCameraId] String value of previous camera Id Guid accessed
   * @param {number} [pageSize] Number of items to retrieve per page
   * @return {PageResultModel[]} Page results object with results property
   */
  public static getCameras(sortField?: string,
    sortOrder?: string,
    page?: number,
    previousCameraId?: string,
    pageSize?: number): Promise<PageResult<CameraSummary>> {

    const sortingField = sortField || null;
    const sortingOrder = sortOrder || null;
    const pageNumber = page || null;
    const pageSizeNumber = pageSize || null;

    const contextData = useContextDataStore();
    const business = contextData.userData.business;
    let resourceUrl = `${BUSINESS_BASE_URL}/${business.businessId}/cameraSummary/`;
    resourceUrl += '?sortField=' + sortingField + '&sortOrder=' + sortingOrder + '&page=' + pageNumber +
      '&pageSize=' + pageSizeNumber;

    return new Promise(function (resolve, reject) {

      apiClient.get(resourceUrl)
        .then(function (serverPageResult) {

          const cameraModels = serverPageResult.results.map(function (cameraSummary) {
            return new CameraSummaryModel(cameraSummary, previousCameraId);
          });

          const pageResult = new PageResultModel(serverPageResult, cameraModels);

          resolve(pageResult);
        })
        .catch((error: ApplicationError) => {
          reject(error);
        });
    });
  }

  /**
   * Retrieve all the camera names for the busines and wrap each camera name up in an object with the
   * property cameraName.
   * @return {Object[]} Array of objects with the property cameraName
   */
  public static getCameraNames() {

    const contextData = useContextDataStore();
    const business = contextData.userData.business;
    const resourceUrl = `${BUSINESS_BASE_URL}/${business.businessId}/cameraName/`;

    return new Promise(function (resolve, reject) {
      apiClient.get(resourceUrl)
        .then(function (serverNames) {

          const nameObjects = serverNames.map(function (serverName) {
            return {
              cameraName: serverName
            };
          });

          resolve(nameObjects);
        })
        .catch((error: ApplicationError) => {
          reject(error);
        });
    });
  }

  public static getRequestAssigneeSummaries() {

    const contextData = useContextDataStore();
    const business = contextData.userData.business;
    const resourceUrl = `${BUSINESS_BASE_URL}/${business.businessId}/requestAssigneeSummary/`;

    return new Promise(function (resolve, reject) {
      apiClient.get(resourceUrl)
        .then(function (serverResponse) {
          const requestAssigneeSummaryModels = serverResponse.results.map(serverModel =>
            new RequestAssigneeSummaryModel(serverModel)
          );

          resolve(requestAssigneeSummaryModels);
        })
        .catch((error: ApplicationError) => {
          reject(error);
        });
    });
  }
}

export default BusinessService;